import { PERMISSION } from '@/app/permissions';
import { Navigate, type RouteObject } from 'react-router-dom';
import { RequirePermission, RequireSession } from '@/app/session';
import {
  CabinetPage,
  ProfilePage,
  EditProfilePage,
  NotificationSettingsPage,
  SavedLibraryPage,
  ApplicationsPage,
  EventDraftEditPage,
  DossierDetailPage,
  PublicationsPage,
  ApplyFundingPage,
  ApplicationsRegisterPage,
  RegisterOrganizationPage,
  AdoptionsPage,
  ResearchOrganizationPage,
  AdvisorProfilePage,
  ProviderPage,
  MyTrainingsPage,
  EventAdminPage,
  MyEventsPage,
  TrainingAdminPage,
  MyTrainingInstitutionPage,
  MyCommunityPage,
  EventEvaluationAnalysisPage,
} from '@/app/routes/cabinetPages';
import { APPLICATIONS_READ_ANY } from '@/features/funding/applicationPermissions';
import { EVALUATION_SCOPE } from '@/features/eventEvaluations/evaluationScope';

/**
 * The cabinet's own screens, inside AppShell — the sidebar's upper section (NAV in
 * shells/cabinet/cabinetNav.ts), plus the pages those link on to.
 */
export const cabinetRoutes: RouteObject[] = [
  // /app itself lands on the overview. This used to be '/' because the router carried a
  // basename of /app, so '/' meant /app/ — now that the prefix is spelled into the routes,
  // leaving it as '/' would have this redirect swallow the public home page.
  { path: '/app', element: <Navigate to="/app/cabinet" replace /> },
  { path: '/app/', element: <Navigate to="/app/cabinet" replace /> },
  {
    path: '/app/cabinet',
    element: (
      <RequireSession>
        <CabinetPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/profil',
    element: (
      <RequireSession>
        <ProfilePage />
      </RequireSession>
    ),
  },
  {
    path: '/app/biblioteca',
    element: (
      <RequireSession>
        <SavedLibraryPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/profil/notificari',
    element: (
      <RequireSession>
        <NotificationSettingsPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/profil/editare',
    element: (
      <RequirePermission permission={PERMISSION.profileEdit}>
        <EditProfilePage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/cereri',
    element: (
      <RequireSession>
        <ApplicationsPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/publicatii',
    element: (
      <RequireSession>
        <PublicationsPage />
      </RequireSession>
    ),
  },
  {
    // The same gate the sidebar entry carries, so the URL typed by hand refuses too rather than
    // rendering a form whose POST comes back 403.
    path: '/app/finantare/aplicare',
    element: (
      <RequirePermission permission={PERMISSION.applicationsCreateOwn}>
        <ApplyFundingPage />
      </RequirePermission>
    ),
  },
  {
    // The register of every application (T165). Either permission is enough, as it is on the
    // endpoint behind it: AIPA holds applications.read:any, MAIA_ADMIN applications.approve:any,
    // and a gate naming one would refuse the other the screen the backend lets it read.
    path: '/app/finantare/cereri',
    element: (
      <RequirePermission permission={APPLICATIONS_READ_ANY}>
        <ApplicationsRegisterPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/cercetare',
    element: (
      <RequirePermission permission={PERMISSION.researchContribute}>
        <ResearchOrganizationPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/cercetare/organizatie',
    element: (
      <RequirePermission permission={PERMISSION.researchContribute}>
        <RegisterOrganizationPage />
      </RequirePermission>
    ),
  },
  {
    // ToR §6.7's monitoring flow. Gated on research.adoption_report, not on
    // research.contribute: reporting that somebody else applied a result is a different
    // act from contributing your own institution's work, and the advisors hold it.
    path: '/app/adoptii',
    element: (
      <RequirePermission permission={PERMISSION.researchAdoptionReport}>
        <AdoptionsPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/evenimente',
    element: (
      <RequirePermission permission={PERMISSION.eventsManage}>
        <EventAdminPage />
      </RequirePermission>
    ),
  },
  {
    // The organiser's own events (tor.md §6.11). events.contribute, the permission creating one
    // already needs (EventController#create), not events.manage — that is everyone's events.
    path: '/app/evenimentele-mele',
    element: (
      <RequirePermission permission={PERMISSION.eventsContribute}>
        <MyEventsPage />
      </RequirePermission>
    ),
  },
  {
    // The same analysis over the organiser's own events. events.contribute is seeded to
    // AUTHENTICATED, so any signed-in account reaches it; the endpoint counts only what they authored.
    path: '/app/evenimentele-mele/evaluari',
    element: (
      <RequirePermission permission={PERMISSION.eventsContribute}>
        <EventEvaluationAnalysisPage scope={EVALUATION_SCOPE.mine} />
      </RequirePermission>
    ),
  },
  {
    path: '/app/formare',
    element: (
      <RequirePermission permission={PERMISSION.trainingManage}>
        <TrainingAdminPage />
      </RequirePermission>
    ),
  },
  {
    path: '/app/consilier',
    element: (
      <RequireSession>
        <AdvisorProfilePage />
      </RequireSession>
    ),
  },
  {
    path: '/app/comunitate',
    element: (
      <RequireSession>
        <MyCommunityPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/instruiri',
    element: (
      <RequireSession>
        <MyTrainingsPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/formare-mea',
    element: (
      <RequireSession>
        <MyTrainingInstitutionPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/furnizor',
    element: (
      <RequireSession>
        <ProviderPage />
      </RequireSession>
    ),
  },
];

/**
 * The dossier's single-flow pages, which FlowShell frames instead of AppShell.
 *
 * `:id` addresses a dossier rather than naming a uuid: the number the links are built from today,
 * and the uuid every older bookmark and notification still carries. Both are passed to the
 * instance endpoints exactly as they arrive — see dossierPath.
 */
export const cabinetFlowRoutes: RouteObject[] = [
  {
    path: '/app/cereri/:id/editare',
    element: (
      <RequireSession>
        <EventDraftEditPage />
      </RequireSession>
    ),
  },
  {
    path: '/app/cereri/:id',
    element: (
      <RequireSession>
        <DossierDetailPage />
      </RequireSession>
    ),
  },
];
